// src/components/generic-crud/ExportDialog.js
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    Alert,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import axiosInstance from 'src/utils/axios';

const formatFormFieldValue = (val, field, row) => {
    if (val === undefined || val === null) return '';

    // Handle boolean
    if (typeof val === 'boolean' || field.type === 'switch') {
        return val ? 'Yes' : 'No';
    }

    // Handle arrays
    if (Array.isArray(val)) {
        return val.map(item => {
            if (item && typeof item === 'object') {
                return item.designation_name || 
                       item.state_name || 
                       item.city_name || 
                       item.block_name || 
                       item.locality_name || 
                       item.name || 
                       ((item.firstName !== undefined || item.lastName !== undefined) ? `${item.firstName || ''} ${item.lastName || ''}`.trim() : null) || 
                       item.label || 
                       item.title || 
                       JSON.stringify(item);
            }
            return item;
        }).join(', ');
    }

    // Handle objects
    if (typeof val === 'object') {
        if (val instanceof Date) {
            return val.toISOString().split('T')[0];
        }
        return val.designation_name || 
               val.state_name || 
               val.city_name || 
               val.block_name || 
               val.locality_name || 
               val.name || 
               ((val.firstName !== undefined || val.lastName !== undefined) ? `${val.firstName || ''} ${val.lastName || ''}`.trim() : null) || 
               val.label || 
               val.title || 
               JSON.stringify(val);
    }

    // Date field types
    if (field.type === 'date' || field.type === 'datetime') {
        try {
            const date = new Date(val);
            if (!Number.isNaN(date.getTime())) {
                const day = date.getDate();
                const month = date.getMonth() + 1;
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            }
        } catch (e) {
            // ignore
        }
    }

    // Match select options label
    if (field.options && Array.isArray(field.options)) {
        const matched = field.options.find(opt => opt.value === val);
        if (matched) return matched.label;
    }

    return String(val);
};

export default function ExportDialog({ open, onClose, config, filters, visibleColumns }) {
    const [format, setFormat] = useState('csv');
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);

    const handleExport = async () => {
        try {
            setExporting(true);
            setError(null);

            // Build query parameters from filters
            const params = new URLSearchParams({ format });

            // Add filters to params
            if (filters) {
                Object.entries(filters).forEach(([key, value]) => {
                    if (value && value !== 'all' && value !== '') {
                        params.append(key, value);
                    }
                });
            }

            // Only export visible columns if provided
            if (visibleColumns && visibleColumns.length > 0) {
                params.append('fields', visibleColumns.join(','));
            }

            // Determine base export URL
            const hasCustomExport = !!config.entity?.apiEndpoint?.export;

            if (hasCustomExport) {
                const baseUrl = config.entity.apiEndpoint.export;

                // Handle if the custom export URL already contains query parameters (e.g. ?formId=123)
                const separator = baseUrl.includes('?') ? '&' : '?';
                const finalUrl = `${baseUrl}${separator}${params.toString()}`;

                const response = await axiosInstance.get(finalUrl, { responseType: 'blob' });

                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                // Try to get filename from Content-Disposition header
                let filename = '';
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename=(.+)/);
                    if (filenameMatch && filenameMatch.length > 1) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                if (!filename) {
                    const entityFileName = (config.entity?.namePlural || 'data').toLowerCase().replace(/\s+/g, '-');
                    // If it's a dynamic form export, default to .zip (since our backend changed)
                    // otherwise respect the format radio
                    if (config.entity?.apiEndpoint?.export?.includes('form-data')) {
                        filename = `${entityFileName}-export.zip`;
                    } else {
                        filename = format === 'excel'
                            ? `${entityFileName}-export.xlsx`
                            : `${entityFileName}-export.csv`;
                    }
                }

                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } else {
                // FALLBACK: Client-side export for generic modules (Users, Projects, etc)
                // Determine the columns to export
                let exportCols = [];
                if (config.exportColumns) {
                    exportCols = config.exportColumns;
                } else if (config.form?.fields) {
                    const traverseFields = (fieldsList) => {
                        fieldsList.forEach(field => {
                            if (field.title && Array.isArray(field.fields)) {
                                traverseFields(field.fields);
                            } else if (field.name) {
                                if (field.type !== 'component' && field.type !== 'button' && field.name !== 'password') {
                                    exportCols.push({
                                        id: field.name,
                                        label: field.label || field.name,
                                        type: field.type,
                                        options: field.options,
                                        render: field.render || null
                                    });
                                }
                            }
                        });
                    };
                    traverseFields(config.form.fields);
                } else {
                    exportCols = config.table.columns;
                }

                // Filter columns if we are fallback to table columns (ignoring visibleColumns for full fields export)
                const isCustomOrFormCols = !!config.exportColumns || !!config.form?.fields;
                if (!isCustomOrFormCols && visibleColumns && visibleColumns.length > 0) {
                    exportCols = exportCols.filter(col => visibleColumns.includes(col.id));
                }

                // 1. Fetch all data
                const payload = {
                    page: 1,
                    limit: 0,
                };

                // Add filters
                if (filters) {
                    Object.entries(filters).forEach(([key, value]) => {
                        if (value && value !== 'all' && value !== '') {
                            payload[key] = value;
                        }
                    });
                }

                // Collect selectedFields to tell the backend what to fetch/project
                const selectedFieldsSet = new Set();
                exportCols.forEach(col => {
                    // Get top-level path of nested properties (e.g. bankDetails from bankDetails.accountNumber)
                    const parts = col.id.split('.');
                    selectedFieldsSet.add(parts[0]);
                });
                // Ensure essential fields are returned
                selectedFieldsSet.add('firstName');
                selectedFieldsSet.add('lastName');
                selectedFieldsSet.add('employeeId');
                
                payload.selectedFields = Array.from(selectedFieldsSet);

                let fetchResponse;
                try {
                    fetchResponse = await axiosInstance.post(config.entity.apiEndpoint.list, payload);
                } catch (err) {
                    console.error('Export POST fallback to GET. Error:', err);
                    fetchResponse = await axiosInstance.get(config.entity.apiEndpoint.list, { params: payload });
                }

                let rowData = fetchResponse.data?.data || [];

                if (config.transformTableData) {
                    rowData = config.transformTableData(rowData);
                }

                // Format data identically to table
                const formattedData = rowData.map(row => {
                    const newRow = {};
                    exportCols.forEach(col => {
                        // Extract value supporting nested objects (e.g. row.permanentAddress.street)
                        let val = col.id.split('.').reduce((acc, part) => acc && acc[part], row);
                        
                        if (col.render) {
                            val = col.render(val, row);
                        } else {
                            val = formatFormFieldValue(val, col, row);
                        }

                        // Extract text if it's still an object
                        if (val && typeof val === 'object') {
                            // Check for React element ($$typeof) — skip render output and use raw value
                            if (val.$$typeof) {
                                val = col.id.split('.').reduce((acc, part) => acc && acc[part], row);
                                if (typeof val === 'object' && val !== null) {
                                    val = val.name || val.label || val.title || '';
                                }
                            } else if (val instanceof Date) {
                                val = val.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                            } else {
                                val = val.label || val.name || val.title || JSON.stringify(val);
                            }
                        }

                        // Handle empty arrays for geography fields
                        if (val === '' && Array.isArray(col.id.split('.').reduce((acc, part) => acc && acc[part], row))) {
                            if (col.id === 'states') val = 'All States';
                            else if (col.id === 'cities') val = 'All Cities';
                        }

                        newRow[col.label || col.id] = val;
                    });
                    return newRow;
                });

                const { utils, writeFile } = await import('xlsx');
                const ws = utils.json_to_sheet(formattedData);
                const wb = utils.book_new();
                utils.book_append_sheet(wb, ws, config.entity.namePlural || 'Data');

                const entityFileName = (config.entity?.namePlural || 'data').toLowerCase().replace(/\s+/g, '-');

                if (format === 'excel') {
                    writeFile(wb, `${entityFileName}-export.xlsx`);
                } else {
                    writeFile(wb, `${entityFileName}-export.csv`, { bookType: 'csv' });
                }
            }

            // Close dialog after successful export
            setTimeout(() => {
                onClose();
            }, 500);

        } catch (err) {
            console.error('Export error:', err);
            setError('Failed to export data. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const handleClose = () => {
        if (!exporting) {
            setFormat('csv');
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="solar:export-bold" width={24} />
                    <Typography variant="h6">Export {config.entity.namePlural}</Typography>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary">
                        Choose the format for exporting your data
                    </Typography>

                    <RadioGroup value={format} onChange={(e) => setFormat(e.target.value)}>
                        <FormControlLabel
                            value="csv"
                            control={<Radio />}
                            label={
                                <Stack>
                                    <Typography variant="subtitle2">CSV (Comma-Separated Values)</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Simple text format, compatible with all spreadsheet applications
                                    </Typography>
                                </Stack>
                            }
                        />
                        <FormControlLabel
                            value="excel"
                            control={<Radio />}
                            label={
                                <Stack>
                                    <Typography variant="subtitle2">Excel (.xlsx)</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Formatted Excel file with styled headers and date formatting
                                    </Typography>
                                </Stack>
                            }
                        />
                    </RadioGroup>

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={exporting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleExport}
                    disabled={exporting}
                    startIcon={exporting ? <CircularProgress size={20} /> : <Iconify icon="solar:download-bold" />}
                >
                    {exporting ? 'Exporting...' : 'Export'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ExportDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    config: PropTypes.object.isRequired,
    filters: PropTypes.object,
    visibleColumns: PropTypes.arrayOf(PropTypes.string),
};
